<script setup lang="ts">
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import FileReport from "@/components/FileReport.vue";
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { Carousel, Slide, Pagination } from "vue3-carousel";
import "vue3-carousel/dist/carousel.css";
import { useStatistics } from "@/composables/useStatistics";
import type { Statistics } from "@/services/statisticsService";
import { logDebug } from "@/utils/secureLogger";

const router = useRouter();

// Navigation functions
const goToStudentSignup = () => {
  router.push({ name: "register-role" }); // Redirect to register role selection
};

const goToTutorSignup = () => {
  router.push({ name: "register-role" }); // Redirect to register role selection
};

// Reactive numbers for counting animation
const tutorsCount = ref(0);
const studentsCount = ref(0);
const ratingCount = ref(0);

// Target values - will be loaded from database (start at 0 to avoid showing stale data)
const tutorsTarget = ref(0);
const studentsTarget = ref(0);
const ratingTarget = ref(0);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const toSafeNumber = (value: unknown, fallback: number): number =>
  isFiniteNumber(value) ? value : fallback;

const { statistics, loadStatistics } = useStatistics();

const syncStatisticsTargets = (stats: Statistics | null) => {
  if (!stats) {
    return;
  }

  // Always use fresh values from database, no fallbacks
  tutorsTarget.value = toSafeNumber(stats.totalTutors, 0);
  studentsTarget.value = toSafeNumber(stats.totalStudents, 0);
  ratingTarget.value = toSafeNumber(stats.averageRating, 0);

  logDebug("Homepage statistics synced", {
    tutors: tutorsTarget.value,
    students: studentsTarget.value,
    rating: ratingTarget.value,
  });
};
watch(
  statistics,
  (stats) => {
    syncStatisticsTargets(stats);
  },
  { immediate: true }
);

// Animation state
const isAnimating = ref(false);
const infoSectionRef = ref<HTMLElement>();

// Intersection Observer for triggering animation when info section is visible
let observer: IntersectionObserver | null = null;

// Counting animation function
const animateCount = (
  current: { value: number },
  target: number,
  duration: number = 2000,
  isDecimal: boolean = false
) => {
  const start = performance.now();
  const startValue = current.value;

  const sanitizedTarget = Number.isFinite(target) ? target : startValue;
  const range = sanitizedTarget - startValue;

  const animate = (currentTime: number) => {
    const elapsed = currentTime - start;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function (ease-out)
    const easedProgress = 1 - Math.pow(1 - progress, 3);

    const nextValue = startValue + range * easedProgress;

    if (isDecimal) {
      current.value = Math.round(nextValue * 10) / 10;
    } else {
      current.value = Math.floor(nextValue);
    }

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      current.value = sanitizedTarget;
    }
  };

  requestAnimationFrame(animate);
};

// Tutor data for carousel
const tutors = ref([
  {
    id: 1,
    name: "Maria Santos",
    description: "Makes math fun and easy!",
    rating: 4.9,
    sessions: 150,
    avatar: "/people-icon.png", // placeholder - you can replace with actual tutor images
  },
  {
    id: 2,
    name: "John Lloyd Cruz",
    description: "Simplifies complex science!",
    rating: 4.9,
    sessions: 150,
    avatar: "/people-icon.png",
  },
  {
    id: 3,
    name: "Sarah Kim",
    description: "Makes writing enjoyable!",
    rating: 4.9,
    sessions: 150,
    avatar: "/people-icon.png",
  },
]);

// Carousel settings
const carouselSettings = {
  itemsToShow: 3,
  wrapAround: false,
  snapAlign: "center" as const,
  transition: 500,
  mouseDrag: false,
  touchDrag: false,
};

// Start counting animation
const startCounting = () => {
  if (isAnimating.value) return;

  // Only animate if we have valid data loaded (not zeros)
  if (tutorsTarget.value === 0 && studentsTarget.value === 0 && ratingTarget.value === 0) {
    return;
  }

  isAnimating.value = true;

  // Reset counts
  tutorsCount.value = 0;
  studentsCount.value = 0;
  ratingCount.value = 0;

  const safeTutorTarget = isFiniteNumber(tutorsTarget.value) ? tutorsTarget.value : 0;
  const safeStudentTarget = isFiniteNumber(studentsTarget.value) ? studentsTarget.value : 0;
  const safeRatingTarget = isFiniteNumber(ratingTarget.value) ? ratingTarget.value : 0;

  // Start animations with slight delays for staggered effect
  setTimeout(() => animateCount(tutorsCount, safeTutorTarget, 2000), 0);
  setTimeout(() => animateCount(studentsCount, safeStudentTarget, 2000), 200);
  setTimeout(() => animateCount(ratingCount, safeRatingTarget, 2000, true), 400);
};

// Setup intersection observer
onMounted(async () => {
  // Set document title
  document.title = "ElevatED – Homepage";

  // Preload critical LCP images for homepage
  const preloadLink1 = document.createElement("link");
  preloadLink1.rel = "preload";
  preloadLink1.as = "image";
  preloadLink1.href = "/orangebox.webp";
  preloadLink1.type = "image/webp";
  document.head.appendChild(preloadLink1);

  const preloadLink2 = document.createElement("link");
  preloadLink2.rel = "preload";
  preloadLink2.as = "image";
  preloadLink2.href = "/homepage-bg.webp";
  preloadLink2.type = "image/webp";
  document.head.appendChild(preloadLink2);

  // CRITICAL: Force fresh statistics load immediately, bypass cache
  // This ensures users always see real-time data, not stale cached values
  await loadStatistics({ force: true });

  // Setup intersection observer for animation
  if (infoSectionRef.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isAnimating.value) {
            startCounting();
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of info section is visible
        rootMargin: "0px 0px -100px 0px", // Start animation a bit before section is fully visible
      }
    );

    observer.observe(infoSectionRef.value);
  }
});

// Cleanup
onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>

<template>
  <!-- First background image as separate div -->
  <main class="flex-1 min-h-[60vh] md:min-h-[80vh] relative bg-white">
    <!-- Floating File Report Component -->
    <FileReport />

    <!-- Rest of the template content... -->
    <div class="homepage-bg-wrapper w-full bg-contain bg-no-repeat bg-top min-h-screen">
      <div class="homepage min-h-screen w-full relative flex flex-col">
        <Header />
        <div class="content-container">
          <div class="studygame-section w-full">
            <div
              class="levelUpYour w-[90%] left-[50%] -translate-x-1/2 text-[40px] h-[80px] absolute top-[80px] justify-center lg:w-[696px] lg:left-[130px] lg:translate-x-0 lg:justify-start lg:text-[80px] lg:h-[149px] lg:top-[240px] leading-[20px] font-black font-['Inter'] text-white text-center lg:text-left flex items-center"
            >
              Level Up Your
            </div>

            <div class="studygame-container">
              <div class="relative w-[70%] md:w-[600px] md:mx-0">
                <picture>
                  <source srcset="/orangebox.webp" type="image/webp" />
                  <img
                    src="/orangebox.png"
                    alt="Orange Box"
                    class="w-[92%] mx-0 brightness-110"
                    width="600"
                    height="300"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                  />
                </picture>
                <div class="study-game-text">Study Game</div>
              </div>
            </div>
          </div>

          <div class="findTheRight">
            Find the right tutor and boost your academic performance—one session at a time.
          </div>

          <div class="buttons-container">
            <button @click="goToStudentSignup" class="rectangleIcon button-blue">
              Find My Tutor
            </button>
            <button @click="goToTutorSignup" class="rectangleIcon button-purple">
              Become A Tutor
            </button>
          </div>

          <!-- Mobile Woman Image - Only visible on mobile (max-width: 480px) -->
          <div class="mobile-woman-container">
            <picture>
              <source
                media="(max-width: 480px)"
                srcset="/optimized/elevated_woman_w_480.webp"
                type="image/webp"
              />
              <source
                media="(max-width: 768px)"
                srcset="/optimized/elevated_woman_w_768.webp"
                type="image/webp"
              />
              <source srcset="/elevated_woman_optimized.webp" type="image/webp" />
              <img
                src="/optimized/elevated_woman_w_480.webp"
                alt="Student using ElevatED platform"
                class="mobile-woman-image"
                loading="eager"
                width="503"
                height="754"
                fetchpriority="high"
              />
            </picture>
          </div>
        </div>

        <!-- 3 Containers - 3 columns, 1 row -->
        <div class="info-section" ref="infoSectionRef">
          <div class="info-containers-wrapper">
            <!--col 1; Tutors Available-->
            <div class="info-container">
              <img
                src="/people-icon.png"
                class="info-icon"
                alt="People Icon"
                width="120"
                height="120"
                loading="lazy"
              />
              <div class="responsive-number">{{ tutorsCount }}</div>
              <div class="info-text">Tutors Available</div>
            </div>

            <!--col 2; Students Learning-->
            <div class="info-container">
              <img
                src="/grad-icon.png"
                class="info-icon"
                alt="Graduated Person Icon"
                width="120"
                height="120"
                loading="lazy"
              />
              <div class="responsive-number">{{ studentsCount }}</div>
              <div class="info-text">Students Learning</div>
            </div>

            <!--col 3; Average Tutor Rating-->
            <div class="info-container">
              <img
                src="/star-icon.png"
                class="info-icon"
                alt="Star Icon"
                width="120"
                height="120"
                loading="lazy"
              />
              <div class="responsive-number">{{ ratingCount }}</div>
              <div class="info-text">Average Tutor Rating</div>
            </div>
          </div>
        </div>

        <!--Why Students Love Us Section-->
        <div class="WhyStudentsSection">
          <!--Title Section-->
          <div class="WhyTitleSection">
            <span class="WhyStudentsTitle">Why Students</span>
            <div class="LoveUsContainer">
              <img
                src="/bluebox.png"
                alt="Blue Box"
                class="LoveUsBackground"
                width="400"
                height="200"
                loading="lazy"
              />
              <span class="LoveUsTitle">Love Us</span>
            </div>
          </div>
          <div class="WhyStudentsDescription">
            We've got everything you need to crush your subjects and have fun while doing it!
          </div>

          <!--Gradient Containers-->
          <div class="gradient-section">
            <!-- Row 1: 3 columns -->
            <div class="gradient-containers-wrapper">
              <!-- Row 1 Column 1 -->
              <div class="gradient-container blue-gradient">
                <div class="gradient-content">
                  <img
                    src="/magnify.png"
                    class="gradient-icon"
                    alt="Magnifying glass icon"
                    width="80"
                    height="80"
                    loading="lazy"
                  />
                  <h3>Smart Tutor Matching</h3>
                  <p>
                    We can find you the perfect tutor match! We know exactly who can help you ace
                    that subject!
                  </p>
                </div>
              </div>

              <!-- Row 1 Column 2 -->
              <div class="gradient-container orange-gradient">
                <div class="gradient-content">
                  <img
                    src="/calendar.png"
                    class="gradient-icon"
                    alt="Calendar icon"
                    width="80"
                    height="80"
                    loading="lazy"
                  />
                  <h3>Flexible Scheduling</h3>
                  <p>
                    Schedule appointments that fit YOUR schedule! Morning person? Night owl? Tutors are
                    ready when you are!
                  </p>
                </div>
              </div>

              <!-- Row 1 Column 3 -->
              <div class="gradient-container purple-gradient">
                <div class="gradient-content">
                  <img
                    src="/message.png"
                    class="gradient-icon"
                    alt="Message icon"
                    width="80"
                    height="80"
                    loading="lazy"
                  />
                  <h3>Chat with Tutors</h3>
                  <p>
                    Got questions for your tutor?<br />Connect and be able to get professional help
                    anytime you need!
                  </p>
                </div>
              </div>
            </div>

            <!-- Row 2: 3 columns -->
            <div class="gradient-containers-wrapper">
              <!-- Row 2 Column 1 -->
              <div class="gradient-container pink-gradient">
                <div class="gradient-content">
                  <img
                    src="/robot.png"
                    class="gradient-icon"
                    alt="AI robot icon"
                    width="80"
                    height="80"
                    loading="lazy"
                  />
                  <h3>AI Study Notes</h3>
                  <p>
                    Get instant study notes, flashcards, and summaries after every session! It's
                    like a personal study assistant!
                  </p>
                </div>
              </div>

              <!-- Row 2 Column 2 -->
              <div class="gradient-container green-gradient">
                <div class="gradient-content">
                  <img
                    src="/best.png"
                    class="gradient-icon"
                    alt="Best quality icon"
                    width="80"
                    height="80"
                    loading="lazy"
                  />
                  <h3>Only the Best Tutors</h3>
                  <p>
                    All our tutors are verified and rated by students like you! No fake profiles,
                    just real people proficient in their fields!
                  </p>
                </div>
              </div>

              <!-- Row 2 Column 3 -->
              <div class="gradient-container yellow-gradient">
                <div class="gradient-content">
                  <img
                    src="/shield.png"
                    class="gradient-icon"
                    alt="Security shield icon"
                    width="80"
                    height="80"
                    loading="lazy"
                  />
                  <h3>Safe & Secure</h3>
                  <p>We verify everyone and keep all interactions safe. Study with confidence!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Second background section -->
    <!-- <div
      class="second-bg-section w-full mt-6 bg-[url('/homepage-bg2.png')] bg-contain bg-no-repeat bg-center min-h-screen"
    > -->
    <!--Future Section-->
    <!-- <div class="FutureSection">
        <div class="FutureTitle-Section">
          <div class="full-title-container">
            <span class="FutureTitle">Meet Your Future </span>
            <div class="HighlightContainer">
              <img src="/yellow-highlight.png" alt="Yellow Highlight" class="HighlightBackground" />
              <span class="StudyBuddies">Study Buddies</span>
            </div>
          </div>
        </div>
        <div class="FutureDescription">
          <b>Stuck on a lesson?</b> Meet friendly, skilled tutors who speak your language<br />and
          get your struggles. Whether it's math, science, or just moral support<br />before an exam
          - your next study buddy is just a click away!
        </div> 

        <div class="tutor-carousel-section">
          <Carousel
            v-bind="carouselSettings"
            :breakpoints="{
              768: {
                itemsToShow: 1,
                snapAlign: 'center' as const,
              },
              1024: {
                itemsToShow: 2,
                snapAlign: 'center' as const,
              },
            }"
          >
            <Slide v-for="tutor in tutors" :key="tutor.id">
              <div class="tutor-card">
                <div class="tutor-avatar">
                  <img :src="tutor.avatar" :alt="tutor.name" />
                </div>
                <h3 class="tutor-name">{{ tutor.name }}</h3>
                <p class="tutor-description">{{ tutor.description }}</p>
                <div class="tutor-stats">
                  <div class="rating-badge">
                    <span class="star">★</span>
                    <span class="rating">{{ tutor.rating }}</span>
                  </div>
                  <div class="sessions-badge">
                    <span class="sessions">{{ tutor.sessions }} sessions</span>
                  </div>
                </div>
                <button class="study-together-btn">Let's Study Together!</button>
              </div>
            </Slide>
          </Carousel>
        </div>
      </div>
    </div> -->
    <!--end -->

    <!-- Ready to Elevate Section -->
    <div class="elevate-section">
      <div class="elevate-content">
        <h2 class="elevate-title">Ready to Elevate<br />Your Learning?</h2>
        <p class="elevate-description">
          Thousands of Senior High School students are already part of ElevatED!
        </p>
        <p class="elevate-subtext">Your journey starts here!</p>
        <div class="elevate-buttons">
          <button @click="goToStudentSignup" class="elevate-btn connect-btn">
            Connect with Tutors!
          </button>
          <button @click="goToTutorSignup" class="elevate-btn become-btn">Become a Tutor!</button>
        </div>
        <div class="elevate-features">
          <span class="feature-item">✨ Free to join</span>
          <span class="feature-item">🔒 100% safe</span>
          <span class="feature-item">⚡ Instant matching</span>
        </div>
      </div>
    </div>

    <Footer />
  </main>
</template>

<style scoped>
/* Homepage Background Wrapper - Zoom in on mobile */
.homepage-bg-wrapper {
  background-image: url("/homepage-bg.png");
  background-size: contain;
  background-position: top center;
}

/* WebP support */
.webp .homepage-bg-wrapper {
  background-image: url("/homepage-bg.webp");
}

@media (max-width: 480px) {
  .homepage-bg-wrapper {
    background-size: 200%;
    background-position: top left;
  }
}

/* Tablets (iPad Mini, iPad Air, iPad Pro) - Use v2 background */
@media (max-width: 1024px) and (min-width: 481px) {
  .homepage-bg-wrapper {
    background-image: url("/homepage_bg_v2.png") !important;
    background-size: contain;
    background-position: top center;
  }
}

/* Level Up Your - iPad Mini & Small Tablets */
@media (max-width: 820px) and (min-width: 481px) {
  .levelUpYour {
    font-size: 45px !important;
    left: 72% !important;
    top: 120px !important;
    transform: translateX(-50%) !important;
    text-align: center !important;
    justify-content: center !important;
    width: 90% !important;
  }
}

/* Level Up Your - iPad Air */
@media (max-width: 900px) and (min-width: 821px) {
  .levelUpYour {
    font-size: 55px !important;
    left: 50% !important;
    top: 140px !important;
    transform: translateX(-50%) !important;
    text-align: center !important;
    justify-content: center !important;
    width: 85% !important;
  }
}

/* Level Up Your - iPad Pro */
@media (max-width: 1024px) and (min-width: 901px) {
  .levelUpYour {
    font-size: 65px !important;
    left: 52% !important;
    top: 140px !important;
    transform: translateX(-50%) !important;
    text-align: center !important;
    justify-content: center !important;
    width: 80% !important;
  }
}

/* Level Up Your - Surface Duo (540px width) */
@media (max-width: 600px) and (min-width: 520px) and (orientation: portrait) {
  .levelUpYour {
    font-size: 38px !important;
    left: 70% !important;
    top: 80px !important;
    transform: translateX(-50%) !important;
    text-align: center !important;
    justify-content: center !important;
    width: 75% !important;
  }
}

/* Level Up Your - Surface Pro 7 (912px landscape) */
@media (min-width: 900px) and (max-width: 920px) and (min-height: 1300px) {
  .levelUpYour {
    font-size: 60px !important;
    left: 68% !important;
    top: 160px !important;
    transform: translateX(-50%) !important;
    text-align: center !important;
    justify-content: center !important;
    width: 80% !important;
  }
}

/* Mobile Landscape - Universal for ALL phones */
@media (orientation: landscape) and (max-height: 500px) {
  .levelUpYour {
    font-size: 40px !important;
    left: 70% !important;
    top: 110px !important;
    transform: translateX(-50%) !important;
    width: 80% !important;
    text-align: center !important;
  }

  .studygame-container {
    margin-top: 175px !important;
    right: 33% !important;
    max-width: 90% !important;
    transform: none !important;
  }

  .study-game-text {
    font-size: 40px !important;
    left: 45% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    text-align: center !important;
  }

  .findTheRight {
    font-size: 14px !important;
    left: 33% !important;
    transform: translateX(-50%) !important;
    margin: 20px 0 0 0 !important;
    width: 70% !important;
    text-align: left !important;
    padding: 0 80px !important;
  }

  .buttons-container {
    left: 37% !important;
    transform: translateX(-50%) !important;
    margin: 30px 0 0 0 !important;
    width: 55% !important;
    justify-content: center !important;
    gap: 15px !important;
  }

  .rectangleIcon {
    height: 50px !important;
    min-height: 50px !important;
    font-size: 13px !important;
  }
}

/* iPhone 12 Pro, iPhone 13/14 Standard - Landscape (844x390) */
@media (orientation: landscape) and (min-width: 800px) and (max-width: 900px) and (max-height: 450px) {
  .levelUpYour {
    font-size: 48px !important;
    left: 69% !important;
    top: 130px !important;
  }

  .studygame-container {
    margin-top: 200px !important;
    right: 42% !important;
    max-width: 45% !important;
  }

  .study-game-text {
    font-size: 45px !important;
    left: 45% !important;
  }

  .findTheRight {
    font-size: 16px !important;
    left: 35% !important;
    padding: 0 90px !important;
  }

  .buttons-container {
    left: 38% !important;
    width: 55% !important;
    gap: 18px !important;
  }

  .rectangleIcon {
    height: 55px !important;
    min-height: 55px !important;
    font-size: 14px !important;
  }
}

/* iPhone XR, iPhone 11 - Landscape (896x414) */
@media (orientation: landscape) and (min-width: 850px) and (max-width: 920px) and (max-height: 420px) {
  .levelUpYour {
    font-size: 50px !important;
    left: 70% !important;
    top: 135px !important;
  }

  .studygame-container {
    margin-top: 200px !important;
    right: 41% !important;
  }

  .study-game-text {
    font-size: 47px !important;
    left: 45% !important;
  }

  .findTheRight {
    font-size: 15px !important;
    left: 34% !important;
    padding: 0 115px !important;
  }

  .buttons-container {
    left: 36% !important;
    width: 50% !important;
    gap: 20px !important;
  }

  .rectangleIcon {
    height: 57px !important;
    min-height: 57px !important;
    font-size: 15px !important;
  }
}

/* iPhone 14 Pro Max, iPhone 15 Pro Max - Landscape (932x430) */
@media (orientation: landscape) and (min-width: 900px) and (max-height: 450px) {
  .levelUpYour {
    font-size: 52px !important;
    left: 70% !important;
    top: 140px !important;
  }

  .studygame-container {
    margin-top: 210px !important;
    right: 41% !important;
    max-width: 43% !important;
  }

  .study-game-text {
    font-size: 49px !important;
    left: 49% !important;
  }

  .findTheRight {
    font-size: 16px !important;
    left: 33% !important;
    width: 65% !important;
    padding: 0 100px !important;
  }

  .buttons-container {
    left: 36% !important;
    width: 50% !important;
    gap: 22px !important;
  }

  .rectangleIcon {
    height: 60px !important;
    min-height: 60px !important;
    font-size: 16px !important;
  }
}

/* Mobile Woman Image - Only visible on mobile (max-width: 480px) */
.mobile-woman-container {
  display: none;
  position: relative;
}

@media (max-width: 480px) {
  .mobile-woman-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0 5px;
  }

  .mobile-woman-image {
    width: 100%;
    max-width: none;
    height: auto;
    object-fit: contain;
  }
}

.FutureDescription {
  font-weight: 500;
  font-family: Inter;
  font-size: 18px;
  width: 100%;
  max-width: 900px;
  font-style: italic;
  text-align: center;
  color: black;
  margin: 20px auto;
  padding: 0 20px;
}

@media (max-width: 768px) {
  .FutureDescription {
    width: 95%;
    font-size: 16px !important;
    padding: 0 25px;
    line-height: 1.4;
  }
}

@media (max-width: 480px) {
  .FutureDescription {
    width: 90%;
    font-size: 15px !important;
    padding: 0 20px;
    line-height: 1.5;
  }
}

.StudyBuddies {
  position: relative;
  font-size: 65px;
  display: inline-block;
  font-weight: 600;
  font-family: "Playfair Display";
  font-style: italic;
  color: black;
  z-index: 2;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .StudyBuddies {
    font-size: 40px;
    white-space: normal;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .StudyBuddies {
    font-size: 35px;
    white-space: normal;
    text-align: center;
  }
}

.FutureSection {
  margin-top: 2em;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 0 20px;
  width: 100%;
}

@media (max-width: 768px) {
  .FutureSection {
    margin-top: 1.5em;
    padding: 0 20px;
  }
}

@media (max-width: 480px) {
  .FutureSection {
    margin-top: 1em;
    padding: 0 15px;
  }
}

.FutureTitle-Section {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1200px;
  padding: 0 20px;
  margin: 0 auto;
  margin-top: 0;
}

@media (max-width: 768px) {
  .FutureTitle-Section {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    height: auto;
    padding: 20px 15px;
  }
}

@media (max-width: 480px) {
  .FutureTitle-Section {
    flex-direction: column;
    align-items: center;
    gap: 10px;
    height: auto;
    padding: 15px 10px;
  }
}

.full-title-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 15px;
}

@media (max-width: 768px) {
  .full-title-container {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .full-title-container {
    flex-direction: column;
    align-items: center;
    gap: 10px;
    text-align: center;
  }
}

.FutureTitle {
  font-size: 65px;
  line-height: 1;
  font-weight: 900;
  font-family: Inter;
  color: black;
  white-space: nowrap;
  display: inline-block;
}

@media (max-width: 768px) {
  .FutureTitle {
    font-size: 40px;
    text-align: center;
    white-space: normal;
  }
}

@media (max-width: 480px) {
  .FutureTitle {
    font-size: 35px;
    text-align: center;
    white-space: normal;
  }
}

.HighlightBackground {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-46%, -40%);
  width: 70em;
  height: auto;
  max-width: 100%;
  z-index: 1;
}

@media (max-width: 768px) {
  .HighlightBackground {
    width: 45em;
    transform: translate(-50%, -40%);
  }
}

@media (max-width: 480px) {
  .HighlightBackground {
    width: 35em;
    transform: translate(-50%, -40%);
  }
}

.HighlightContainer {
  position: relative;
  display: inline-block;
  width: 33em;
}

@media (max-width: 768px) {
  .HighlightContainer {
    width: auto;
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .HighlightContainer {
    width: auto;
    max-width: 90%;
  }
}

.second-bg-section {
  height: auto;
  min-height: 65em;
  padding-bottom: 4em;
}

@media (max-width: 768px) {
  .second-bg-section {
    height: auto;
    min-height: 45em;
    padding-bottom: 3em;
    background-size: cover;
    background-position: center;
  }
}

@media (max-width: 480px) {
  .second-bg-section {
    height: auto;
    min-height: 40em;
    padding-bottom: 2em;
    background-size: cover;
    background-position: center;
  }
}

body {
  background-color: white;
}

h3 {
  width: 100%;
  max-width: 300px;
  font-size: 24px;
  font-family: Inter;
  font-weight: 800;
  color: #000;
  text-align: center;
  margin: 10px auto 15px auto;
  height: 20px;
  padding: 0 15px;
}

p {
  width: 100%;
  max-width: 800px;
  font-family: Inter;
  font-weight: 400;
  color: #333;
  text-align: center;
  margin: 5px auto;
  line-height: 1.5;
  padding: 0 20px;
}

.pink-gradient {
  background: linear-gradient(180deg, #ff52b2, rgba(217, 217, 217, 0));
}

.green-gradient {
  background: linear-gradient(180deg, #77de5d, rgba(217, 217, 217, 0));
}

.yellow-gradient {
  background: linear-gradient(180deg, #feef5e, rgba(217, 217, 217, 0));
}

.gradient-section {
  margin-top: 2em;
  margin-bottom: 4em;
}

.gradient-containers-wrapper {
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 30px;
  width: 100%;
  margin-bottom: 30px;
}

@media (max-width: 480px) {
  .gradient-containers-wrapper {
    flex-direction: column;
    gap: 20px;
    padding: 0 30px;
    justify-content: center;
    max-width: 360px;
    margin: 0 auto 30px auto;
  }
  .gradient-container h3 {
    font-size: 23px;
    margin-bottom: 15px;
    padding: 0;
  }
  .gradient-container p {
    font-size: 15px !important;
  }
}

/* Gradient Cards - iPad Mini & Small Tablets */
@media (max-width: 821px) and (min-width: 481px) {
  .gradient-containers-wrapper {
    flex-direction: column;
    gap: 20px;
    padding: 0 30px;
    justify-content: center;
    max-width: 700px;
    margin: 0 auto 30px auto;
  }
  .gradient-container {
    flex: 1 1 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  .gradient-container h3 {
    font-size: 24px !important;
    margin-bottom: 15px !important;
    padding: 0 !important;
  }
  .gradient-container p {
    font-size: 18px !important;
  }
}

/* Gradient Cards - iPad Air */
@media (max-width: 900px) and (min-width: 820px) {
  .gradient-containers-wrapper {
    flex-direction: column;
    gap: 25px;
    padding: 0 35px !important;
    justify-content: center;
    max-width: 740px;
    margin: 0 auto 30px auto;
  }
  .gradient-container {
    flex: 1 1 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  .gradient-container h3 {
    font-size: 26px !important;
    margin-bottom: 18px !important;
    padding: 0 !important;
  }
  .gradient-container p {
    font-size: 19px !important;
  }
}

/* Gradient Cards - iPad Pro */
@media (max-width: 1024px) and (min-width: 901px) {
  .gradient-containers-wrapper {
    flex-direction: column;
    gap: 40px;
    padding: 0 60px;
    justify-content: center;
    max-width: 950px;
    margin: 0 auto 30px auto;
  }
  .gradient-container {
    flex: 1 1 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  .gradient-container h3 {
    font-size: 28px !important;
    margin-bottom: 20px !important;
    padding: 0 !important;
  }
  .gradient-container p {
    font-size: 20px !important;
  }
}

@media (max-width: 480px) {
  .gradient-containers-wrapper {
    flex-direction: column;
    gap: 20px;
    padding: 0 20px;
  }
}

.gradient-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 20px;
  border-radius: 20px;
  flex: 1;
  max-width: 400px;
  width: 100%;
  position: relative;
  min-height: 300px;
}

.gradient-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  position: relative;
}

.gradient-icon {
  border-radius: 20px;
  width: 80px;
  height: 80px;
  margin-top: 20px;
}

.blue-gradient {
  background: linear-gradient(180deg, #1a7dff, rgba(217, 217, 217, 0));
}

.orange-gradient {
  background: linear-gradient(180deg, #ff9730, rgba(217, 217, 217, 0));
}

.purple-gradient {
  width: 100%;
  position: relative;
  border-radius: 20px;
  background: linear-gradient(180deg, #9131ff, rgba(231, 210, 255, 0));
  height: 260px;
}

.info-section {
  margin-top: 15em;
  margin-bottom: 10em;
  min-height: 300px;
}

.info-containers-wrapper {
  min-height: 250px;
}

@media (max-width: 768px) {
  .info-section {
    margin-top: 8em;
    margin-bottom: 6em;
    padding: 0 20px;
  }
}

@media (max-width: 480px) {
  .info-section {
    margin-top: 2em;
    margin-bottom: 3em;
    padding: 0 30px;
    min-height: 600px;
  }

  .info-containers-wrapper {
    min-height: 500px;
  }
}

.info-containers-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 50px;
  width: 100%;
}

/* Info Section - iPad Mini & Small Tablets */
@media (max-width: 819px) and (min-width: 481px) {
  .info-containers-wrapper {
    flex-direction: column;
    gap: 40px;
    max-width: 650px;
    padding: 0 20px;
    margin: 0 auto;
  }
  .info-section {
    margin-top: 100px !important;
  }

  .info-container {
    max-width: 100% !important;
    width: 100% !important;
  }
}

/* Info Section - iPad Air */
@media (max-width: 900px) and (min-width: 820px) {
  .info-containers-wrapper {
    flex-direction: column;
    gap: 35px;
    max-width: 730px;
    padding: 0 30px;
    margin: 0 auto;
  }
  .info-section {
    margin-top: 100px !important;
    margin-bottom: 80px !important;
  }

  .info-container {
    max-width: 100% !important;
    width: 100% !important;
  }

  .WhyStudentsTitle {
    margin-left: 80px;
  }

  .WhyStudentsDescription {
    font-size: 21px !important;
    padding: 0 60px 0 60px !important;
    margin-top: -20px !important;
    margin-bottom: 60px !important;
  }
}

/* Info Section - iPad Pro */
@media (max-width: 1024px) and (min-width: 901px) {
  .info-containers-wrapper {
    flex-direction: column;
    gap: 40px;
    max-width: 900px;
    padding: 0 40px;
    margin: 0 auto;
  }
  .info-section {
    margin-top: 110px !important;
    margin-bottom: 100px !important;
  }

  .info-container {
    max-width: 100% !important;
    width: 100% !important;
  }

  .WhyStudentsDescription {
    font-size: 20px !important;
    width: 90% !important;
  }

  .WhyStudentsTitle {
    margin-left: 30px;
  }
}

@media (max-width: 480px) {
  .info-containers-wrapper {
    flex-direction: column;
    gap: 30px;
  }
}

.info-icon {
  width: 70px;
  margin-top: 18px;
}

@media (max-width: 480px) {
  .info-icon {
    width: 50px !important;
    margin-top: 10px !important;
  }

  .responsive-number {
    font-size: 40px !important;
    width: 140px !important;
    height: 60px !important;
  }

  .info-text {
    font-size: 14px !important;
  }
}

.info-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  flex: 1;
  max-width: 400px;
  width: 100%;
  position: relative;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
  border-radius: 30px;
  background-color: #fff;
  height: 260px;
}

.info-icon {
  width: 70px;
  margin-top: 18px;
}

.WhyTitleSection {
  display: flex;
  align-items: baseline;
  gap: 20px;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
  height: 130px;
}

@media (max-width: 768px) {
  .WhyTitleSection {
    flex-direction: column;
    align-items: center;
    gap: 10px;
    height: auto;
    padding: 20px;
  }
}

.WhyStudentsTitle {
  font-size: 70px;
  line-height: 1;
  font-weight: 900;
  font-family: Inter;
  color: #000;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .WhyStudentsTitle {
    font-size: 45px;
    text-align: center;
  }
}

@media (max-width: 820px) and (min-width: 481px) {
  .WhyStudentsTitle {
    font-size: 50px !important;
  }

  .LoveUsTitle {
    font-size: 60px !important;
  }

  .WhyStudentsDescription {
    font-size: 22px !important;
  }
}

.LoveUsContainer {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.LoveUsBackground {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: auto;
  height: auto;
  max-width: 100%;
  z-index: 1;
}

.LoveUsTitle {
  font-size: 80px;
  font-style: italic;
  line-height: 1;
  font-weight: 600;
  font-family: "Playfair Display";
  color: #5271ff;
  white-space: nowrap;
  height: 126px;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 0 20px;
}

@media (max-width: 768px) {
  .LoveUsTitle {
    font-size: 50px;
    height: auto;
    padding: 10px;
  }
}

.WhyStudentsDescription {
  width: 800px;
  height: 60px;
  margin: 0 auto 10px auto;
  font-size: 18px;
  font-weight: 400;
  font-family: Inter;
  font-style: italic;
  color: #000;
  text-align: center;
  display: block;
}

@media (max-width: 768px) {
  .WhyStudentsDescription {
    width: 90%;
    height: auto;
    font-size: 16px;
    padding: 0 20px;
  }
}

.responsive-number {
  width: 176px;
  position: relative;
  font-size: 60px;
  font-weight: 900;
  font-family: Inter;
  color: #5271ff;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 77px;
}

.info-text {
  width: 250px;
  position: relative;
  font-size: 20px;
  display: inline-block;
  font-family: Inter;
  color: #0d0d0d;
  text-align: center;
  height: 40px;
}

.studygame-container {
  position: relative;
  margin-top: 180px;
  width: auto;
  display: flex;
  justify-content: center;
  padding: 0 0px;
}

@media (min-width: 1025px) {
  .studygame-container {
    margin-top: 360px;
    padding: 0;
    justify-content: flex-start;
    margin-left: 200px;
  }
}

/* Study Game Container - iPad Mini & Small Tablets */
@media (max-width: 820px) and (min-width: 481px) {
  .studygame-container {
    margin-top: 190px;
    margin-left: 30%;
    transform: translateX(-50%);
    max-width: 50%;
    padding: 0 20px;
  }
}

/* Study Game Container - iPad Air */
@media (max-width: 900px) and (min-width: 821px) {
  .studygame-container {
    margin-top: 220px;
    margin-left: 50%;
    transform: translateX(-50%);
    max-width: 55%;
    padding: 0 20px;
  }
}

/* Study Game Container - iPad Pro */
@media (max-width: 1024px) and (min-width: 901px) {
  .studygame-container {
    margin-top: 250px;
    margin-left: 50%;
    transform: translateX(-30%);
    max-width: 65%;
    padding: 0 20px;
  }
  .study-game-text {
    left: 45% !important;
  }
}

/* Study Game Container - Surface Duo (540px portrait) */
@media (max-width: 600px) and (min-width: 520px) and (orientation: portrait) {
  .studygame-container {
    margin-top: 150px;
    margin-left: 30%;
    transform: translateX(-50%);
    max-width: 70%;
    padding: 0 20px;
  }
  .study-game-text {
    font-size: 35px !important;
    left: 45% !important;
  }
}

/* Study Game Container - Surface Pro 7 (912px landscape) */
@media (min-width: 900px) and (max-width: 920px) and (min-height: 1300px) {
  .studygame-container {
    margin-top: 240px;
    margin-left: 29%;
    transform: translateX(-50%);
    max-width: 50%;
    padding: 0 20px;
  }
  .study-game-text {
    font-size: 58px !important;
    left: 45% !important;
  }
}

@media (max-width: 480px) and (orientation: portrait) {
  .studygame-container {
    margin-top: 150px;
    margin-left: 20px;
    padding: 0 15px;
  }
}

/* iPhone SE and small phones - Portrait */
@media (max-width: 380px) and (orientation: portrait) {
  .studygame-container {
    margin-top: 140px !important;
    margin-left: 15px !important;
    padding: 0 10px !important;
  }
}

.study-game-text {
  width: 100%;
  max-width: 550px;
  position: absolute;
  top: 30%;
  left: 47%;
  transform: translate(-50%, -50%);
  font-size: clamp(40px, 7vw, 80px);
  font-weight: 600;
  font-family: "Playfair Display";
  font-style: italic;
  color: #000;
  text-align: left;
  height: auto;
  min-height: 50px;
  z-index: 10;
}

@media (min-width: 1025px) {
  .study-game-text {
    width: 460px;
    font-size: 85px;
    height: 100px;
  }
}

/* Study Game Text - iPad Mini & Small Tablets */
@media (max-width: 820px) and (min-width: 481px) {
  .study-game-text {
    font-size: 52px;
    left: 43%;
    top: 45%;
    transform: translate(-50%, -50%);
    text-align: center;
    max-width: 90%;
    white-space: nowrap;
  }
}

/* Study Game Text - iPad Air */
@media (max-width: 900px) and (min-width: 821px) {
  .study-game-text {
    font-size: 55px;
    left: 50%;
    top: 45%;
    transform: translate(-50%, -50%);
    text-align: center;
    max-width: 90%;
    white-space: nowrap;
  }
}

/* Study Game Text - iPad Pro */
@media (max-width: 1024px) and (min-width: 901px) {
  .study-game-text {
    font-size: 62px;
    left: 50%;
    top: 45%;
    transform: translate(-50%, -50%);
    text-align: center;
    max-width: 90%;
    white-space: nowrap;
  }
}

/* Study Game Text - Surface Duo */
@media (max-width: 600px) and (min-width: 481px) and (orientation: portrait) {
  .study-game-text {
    font-size: 45px;
    left: 50%;
    top: 45%;
    transform: translate(-50%, -50%);
    text-align: center;
    max-width: 90%;
    white-space: nowrap;
  }
}

@media (max-width: 480px) and (orientation: portrait) {
  .study-game-text {
    font-size: 33px;
    left: 45%;
    top: 50%;
    text-align: center;
    max-width: 85%;
    white-space: nowrap;
  }
}

.findTheRight {
  width: 500px;
  position: relative;
  left: 14%;
  margin-top: 10px;
  font-size: 20px;
  font-weight: 500;
  font-family: Inter;
  color: #fff;
  text-align: left;
  display: inline-block;
  min-height: 40px;
  height: auto;
  contain: layout;
}

/* Find The Right - iPad Mini & Small Tablets */
@media (max-width: 820px) and (min-width: 481px) {
  .findTheRight {
    width: auto;
    max-width: 500px;
    left: 0;
    transform: none;
    margin: 30px 0 0;
    text-align: left;
    font-size: 17px;
    height: auto;
    display: block;
    color: #ffffff !important;
    line-height: 1.5;
    padding: 0 0 0 60px;
  }
}

/* Find The Right - iPad Air */
@media (max-width: 900px) and (min-width: 821px) {
  .findTheRight {
    width: 75%;
    left: 50%;
    transform: translateX(-50%);
    margin: 35px 0 0;
    text-align: center;
    font-size: 18px;
    height: auto;
    display: block;
    color: #ffffff !important;
    line-height: 1.5;
    padding: 0;
  }
}

/* Find The Right - iPad Pro */
@media (max-width: 1024px) and (min-width: 901px) {
  .findTheRight {
    width: auto;
    max-width: 650px;
    left: 60%;
    transform: translateX(-50%);
    margin: 40px 0 0;
    text-align: left;
    font-size: 20px;
    height: auto;
    display: block;
    color: #ffffff !important;
    line-height: 1.5;
    padding: 0 50px;
  }
}

/* Find The Right - Surface Duo */
@media (max-width: 600px) and (min-width: 520px) and (orientation: portrait) {
  .findTheRight {
    width: 85%;
    left: 42%;
    transform: translateX(-50%);
    margin: 22px 0 0;
    font-size: 13px;
    height: auto;
    display: block;
    color: #ffffff !important;
    line-height: 1.4;
    padding: 0 50px;
  }
}

/* Find The Right - Surface Pro 7 */
@media (min-width: 900px) and (max-width: 920px) and (min-height: 1300px) {
  .findTheRight {
    width: 75%;
    left: 35%;
    transform: translateX(-50%);
    margin: 25px 0 0;
    text-align: left;
    font-size: 19px;
    height: auto;
    display: block;
    color: #ffffff !important;
    line-height: 1.5;
    padding: 0 70px;
  }
}

@media (max-width: 480px) and (min-width: 1px) and (orientation: portrait) {
  .findTheRight {
    width: 90%;
    left: 50%;
    transform: translateX(-50%);
    margin: 30px 0 0 0;
    text-align: center;
    font-size: 18px;
    height: auto;
    display: block;
    color: #000 !important;
    line-height: 1.5;
    padding: 0 20px;
  }
}

@media (max-width: 480px) and (orientation: portrait) {
  .findTheRight {
    font-size: 16px;
    margin: 25px 0 0 0;
    padding: 0 15px;
    color: #ffffff !important;
  }
}

.buttons-container {
  display: flex;
  gap: 40px;
  margin-top: 80px;
  position: relative;
  left: 14%;
  max-width: 561px;
}

/* Buttons Container - iPad Mini & Small Tablets */
@media (max-width: 820px) and (min-width: 481px) {
  .buttons-container {
    flex-direction: row;
    gap: 20px;
    left: 7%;
    margin: 60px 0 0;
    width: 50%;
    max-width: 500px;
    justify-content: center;
  }
}

/* Buttons Container - iPad Air */
@media (max-width: 900px) and (min-width: 821px) {
  .buttons-container {
    flex-direction: row;
    gap: 25px;
    left: 50%;
    transform: translateX(-50%);
    margin: 70px 0 0;
    width: 80%;
    max-width: 600px;
    justify-content: center;
  }
}

/* Buttons Container - iPad Pro */
@media (max-width: 1024px) and (min-width: 901px) {
  .buttons-container {
    flex-direction: row;
    gap: 25px;
    left: 65%;
    transform: translateX(-50%);
    margin: 60px 0 0;
    width: 75%;
    max-width: 650px;
    justify-content: center;
  }
}

/* Buttons Container - Surface Duo */
@media (max-width: 600px) and (min-width: 520px) and (orientation: portrait) {
  .buttons-container {
    left: 45%;
    transform: translateX(-50%);
    margin: 35px 0 0;
    width: 80%;
    max-width: 400px;
    justify-content: center;
  }
}

/* Buttons Container - Surface Pro 7 */
@media (min-width: 900px) and (max-width: 920px) and (min-height: 1300px) {
  .buttons-container {
    flex-direction: row;
    gap: 25px;
    left: 40%;
    transform: translateX(-50%);
    margin: 65px 0 0;
    width: 75%;
    max-width: 600px;
    justify-content: center;
  }
}

@media (max-width: 480px) and (orientation: portrait) {
  .buttons-container {
    flex-direction: column;
    gap: 20px;
    left: 50%;
    transform: translateX(-50%);
    margin: 20px 0 0 0;
    width: 85%;
    max-width: 400px;
  }
}

.rectangleIcon {
  width: 100%;
  position: relative;
  border-radius: 40px;
  max-width: 100%;
  overflow: hidden;
  height: 4em;
  border: none;
  cursor: pointer;
  flex: 1;
  color: white;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  transition: opacity 0.3s ease;
  border: 3px solid #000;
  box-shadow: 8px 8px 0px #000;
  min-height: 64px;
}

/* Button styling for iPad Mini & Small Tablets */
@media (max-width: 820px) and (min-width: 481px) {
  .rectangleIcon {
    height: 55px !important;
    min-height: 55px !important;
    font-size: 15px !important;
    border-radius: 30px !important;
    box-shadow: 5px 5px 0px #000 !important;
    padding: 8px 20px !important;
  }
}

/* Button styling for iPad Air */
@media (max-width: 900px) and (min-width: 821px) {
  .rectangleIcon {
    height: 58px !important;
    min-height: 58px !important;
    font-size: 16px !important;
    border-radius: 32px !important;
    box-shadow: 5px 5px 0px #000 !important;
    padding: 8px 22px !important;
  }
}

/* Button styling for iPad Pro */
@media (max-width: 1024px) and (min-width: 901px) {
  .rectangleIcon {
    height: 60px !important;
    min-height: 60px !important;
    font-size: 17px !important;
    border-radius: 33px !important;
    box-shadow: 6px 6px 0px #000 !important;
    padding: 8px 24px !important;
  }
}

@media (max-width: 480px) and (orientation: portrait) {
  .rectangleIcon {
    height: 60px !important;
    min-height: 60px !important;
    font-size: 16px !important;
    border-radius: 30px !important;
    box-shadow: 5px 5px 0px #000 !important;
  }
}

.rectangleIcon:hover {
  opacity: 0.9;
}

.button-blue {
  background: linear-gradient(135deg, #3674ef, #ba25eb);
}

.button-purple {
  background: linear-gradient(135deg, #fb913d, #ed4b95);
}

/* Tutor Carousel Styles */
.tutor-carousel-section {
  width: 100%;
  max-width: 1400px; /* Increased width - edit this value to make carousel wider/narrower */
  margin: 80px auto; /* Increased top/bottom margin for more height spacing */
  margin-left: 100px;
  padding: 0 60px; /* Increased horizontal padding for more width */
}

@media (max-width: 1200px) {
  .tutor-carousel-section {
    margin-left: 50px;
    padding: 0 40px;
    max-width: 1200px;
  }
}

@media (max-width: 800px) {
  .tutor-carousel-section {
    padding: 0 30px; /* Increased mobile padding for larger appearance */
    margin: 60px auto; /* Increased mobile margin */
    margin-left: auto;
    max-width: 100%;
  }
}

.tutor-card {
  background: white;
  border-radius: 20px;
  padding: 50px 25px; /* Increased padding for taller cards */
  margin: 0 20px; /* EDIT THIS VALUE TO ADJUST GAPS BETWEEN CARDS - increase for wider gaps, decrease for tighter gaps */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 450px; /* Increased card height */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: calc(100% - 30px); /* Adjusted width calculation to match new margins */
}

.tutor-card:hover {
  transform: scale(1.05) translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

@media (min-width: 1024px) {
  .content-container {
    width: 900px;
    max-width: 100vw;
    margin: 0 auto;
    position: relative;
    left: -280px;
    right: 0;
  }
  .studygame-section {
    margin-left: -80px !important;
  }
}

.tutor-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #e0e0e0;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.tutor-avatar img {
  width: 60px;
  height: 60px;
  object-fit: cover;
}

.tutor-name {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0 0 0 0;
  font-family: "Inter", sans-serif;
}

.tutor-description {
  font-size: 16px;
  color: #666;
  font-style: italic;
  margin: 0 0 20px 0;
  font-family: "Inter", sans-serif;
}

.tutor-stats {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 25px;
}

.rating-badge {
  background: #fff3cd;
  padding: 8px 15px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.star {
  color: #ffc107;
  font-size: 16px;
}

.rating {
  font-weight: 700;
  color: #333;
  font-size: 14px;
}

.sessions-badge {
  background: #d1ecf1;
  padding: 8px 15px;
  border-radius: 20px;
}

.sessions {
  color: #0c5460;
  font-weight: 600;
  font-size: 14px;
}

.study-together-btn {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Inter", sans-serif;
}

.study-together-btn:hover {
  background: linear-gradient(135deg, #5855eb, #7c3aed);
  transform: translateY(-2px);
}

/* Custom carousel track styles for exact 3-card display */
.carousel__track {
  display: flex !important;
  align-items: center;
  justify-content: center;
}

.carousel__slide {
  /* CARD GAP CONTROL: The margin values here should match the margin in .tutor-card */
  /* Current: 15px margin per card = 30px total gap between cards */
  min-width: calc(33.333% - 30px) !important; /* Adjusted to match new card margins */
  max-width: calc(33.333% - 30px) !important; /* Adjusted to match new card margins */
  flex-shrink: 0;
}

.carousel__viewport {
  overflow: visible;
}

/* Hide navigation arrows completely */
.carousel__next,
.carousel__prev {
  display: none !important;
}

/* Responsive Design */
@media (max-width: 800px) {
  .tutor-card {
    margin: 0 15px; /* EDIT THIS VALUE FOR MOBILE CARD GAPS */
    padding: 30px 20px; /* Increased mobile padding */
    min-height: 400px; /* Increased mobile card height */
  }

  .tutor-name {
    font-size: 22px; /* Slightly larger on mobile */
  }

  .tutor-description {
    font-size: 15px; /* Slightly larger on mobile */
  }

  .study-together-btn {
    padding: 12px 24px; /* Larger button on mobile */
    font-size: 15px; /* Larger button text */
  }
}

@media (max-width: 480px) {
  .tutor-card {
    margin: 0 10px;
    padding: 25px 15px;
    min-height: 350px;
  }

  .tutor-name {
    font-size: 20px;
  }

  .tutor-description {
    font-size: 14px;
  }

  .study-together-btn {
    padding: 10px 20px;
    font-size: 14px;
  }
}

/* Ready to Elevate Section Styles */
.elevate-section {
  background: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%);
  padding: 10px 10px;
  margin: 0 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 600px;
}

.elevate-content {
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.elevate-title {
  font-size: 60px;
  font-weight: 900;
  font-family: "Inter", sans-serif;
  color: white;
  margin: 0 0 30px 0;
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.elevate-description {
  font-size: 19px;
  font-weight: 400;
  font-family: "Inter", sans-serif;
  color: white;
  margin: 0 0 10px 0;
  opacity: 1;
  text-align: center;
  width: 50em !important;
}

.elevate-subtext {
  font-size: 18px;
  font-style: italic;
  font-weight: 700;
  font-family: "Inter", sans-serif;
  color: white;
  margin: 0 0 40px 0;
  opacity: 1;
  text-align: center;
}

.elevate-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 30px;
  margin-bottom: 40px;
  width: 100%;
  flex-wrap: wrap;
}

.elevate-btn {
  padding: 18px 40px;
  border-radius: 30px;
  border: 3px solid #000;
  font-size: 24px;
  font-weight: 700;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 5px 5px 0px #000;
  min-width: 250px;
  text-align: center;
}

.elevate-btn:hover {
  transform: translateY(-2px);
  box-shadow: 7px 7px 0px #000;
}

.connect-btn {
  background: linear-gradient(135deg, #3b82f6, #ac5aff);
  color: white;
  width: 15em;
}

.become-btn {
  background: linear-gradient(135deg, #f97316, #f6338b);
  color: white;
  width: 15em;
}

.elevate-features {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 40px;
  margin-top: 20px;
  width: 100%;
  flex-wrap: wrap;
}

.feature-item {
  font-size: 18px;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
}

/* Mobile Responsive for Elevate Section */
@media (max-width: 768px) {
  .elevate-section {
    padding: 60px 20px;
    margin: 40px 0 0 0;
    min-height: 500px;
  }

  .elevate-content {
    max-width: 100%;
    padding: 0 10px;
  }

  .elevate-title {
    font-size: 40px !important;
    max-width: 10em !important;
    margin-bottom: 20px;
    line-height: 1.2;
  }

  .elevate-description {
    font-size: 18px;
    margin-bottom: 1em !important;
    padding: 0 10px;
    max-width: 20em !important;
  }

  .elevate-subtext {
    font-size: 18px !important;
    margin-bottom: 30px;
    padding: 0 10px;
  }

  .elevate-buttons {
    flex-direction: column;
    gap: 20px;
    align-items: center;
    width: 100%;
  }

  .elevate-btn {
    width: 100%;
    max-width: 320px;
    padding: 16px 32px;
    font-size: 16px;
    min-width: unset;
  }

  .elevate-features {
    flex-direction: row;
    gap: 20px;
    margin-top: 25px;
    justify-content: space-between;
    flex-wrap: nowrap;
  }

  .feature-item {
    font-size: 14px !important;
    width: 1em !important;
    flex: 1;
    min-width: 0;
    justify-content: center;
  }
}

/* Extra small mobile devices */
@media (max-width: 480px) {
  .elevate-section {
    padding: 40px 15px;
    min-height: 450px;
  }

  .elevate-title {
    font-size: 32px;
    margin-bottom: 15px;
  }

  .elevate-description {
    font-size: 16px;
    margin-bottom: 5px;
  }

  .elevate-subtext {
    font-size: 14px;
    margin-bottom: 25px;
  }

  .elevate-btn {
    max-width: 280px;
    padding: 14px 28px;
    font-size: 15px;
  }

  .elevate-features {
    gap: 12px;
    margin-top: 20px;
  }

  .feature-item {
    font-size: 13px;
  }
}

/* Phone Landscape Mode - Elevate Section */
@media (max-width: 896px) and (max-height: 414px) and (orientation: landscape) {
  .elevate-section {
    padding: 30px 15px !important;
    min-height: 350px !important;
  }

  .elevate-title {
    font-size: 26px !important;
    margin-bottom: 10px !important;
  }

  .elevate-description {
    font-size: 14px !important;
    margin-bottom: 5px !important;
    max-width: 30em !important;
  }

  .elevate-subtext {
    font-size: 13px !important;
    margin-bottom: 15px !important;
  }

  .elevate-buttons {
    flex-direction: row !important;
    gap: 15px !important;
    margin-bottom: 20px !important;
  }

  .elevate-btn {
    padding: 10px 20px !important;
    font-size: 13px !important;
    max-width: 200px !important;
    min-width: 180px !important;
  }

  .elevate-features {
    gap: 15px !important;
    margin-top: 10px !important;
  }

  .feature-item {
    font-size: 11px !important;
  }
}
</style>
